/**
 * BestCreatives — Najlepsze kreacje reklamowe
 * Displays ranked ad creatives (images + videos) with performance metrics,
 * preview thumbnails, and actions to generate variations.
 * Includes a "Hidden Gems" tab for low-spend high-efficiency ads.
 */
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Image,
  Video,
  Gem,
  ExternalLink,
  Wand2,
  DollarSign,
  Eye,
  MousePointer,
  TrendingUp,
  ShoppingCart,
  Target,
  BarChart3,
  Sparkles,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

// ── Types ──────────────────────────────────────────────

interface RankedCreative {
  adId: string;
  adName: string;
  adSetName: string;
  campaignName: string;
  mediaType: "image" | "video";
  imageUrl: string | null;
  thumbnailUrl: string | null;
  videoId: string | null;
  primaryText: string;
  headline: string;
  callToAction: string;
  spend: number;
  impressions: number;
  clicks: number;
  ctr: number;
  cpc: number;
  reach: number;
  leads: number;
  costPerLead: number;
  purchases: number;
  purchaseValue: number;
  costPerPurchase: number;
  roas: number;
  rankScore: number;
  rankMetricLabel: string;
}

type CreativeFilter = "all" | "image" | "video" | "hidden_gems";

type SortKey =
  | "rank"
  | "leads"
  | "costPerLead"
  | "spend"
  | "clicks"
  | "ctr"
  | "cpc"
  | "impressions"
  | "purchases"
  | "costPerPurchase"
  | "roas"
  | "purchaseValue";

type SortDir = "asc" | "desc";

interface BestCreativesProps {
  adAccountId: string;
  datePreset: string;
  customRange?: { since: string; until: string };
  forClientUserId?: string;
  dashboardMode?: string;
}

// ── Constants ──────────────────────────────────────────

const FILTER_CONFIG: Record<CreativeFilter, { labelKey: string; icon: typeof Image }> = {
  all: { labelKey: "dashboard.roasWorld.creatives.tabAll", icon: BarChart3 },
  image: { labelKey: "dashboard.roasWorld.creatives.tabGraphics", icon: Image },
  video: { labelKey: "dashboard.roasWorld.creatives.tabVideo", icon: Video },
  hidden_gems: { labelKey: "dashboard.roasWorld.creatives.tabHiddenGems", icon: Gem },
};

const SORT_CONFIG: Record<
  SortKey,
  { labelKey: string; defaultDir: SortDir; getValue: (c: RankedCreative) => number }
> = {
  rank: {
    labelKey: "dashboard.roasWorld.creatives.sortRank",
    defaultDir: "desc",
    getValue: (c) => c.rankScore,
  },
  leads: {
    labelKey: "dashboard.roasWorld.creatives.sortLeads",
    defaultDir: "desc",
    getValue: (c) => c.leads,
  },
  costPerLead: {
    labelKey: "dashboard.roasWorld.creatives.sortCostPerLead",
    defaultDir: "asc",
    getValue: (c) => c.costPerLead,
  },
  spend: {
    labelKey: "dashboard.roasWorld.creatives.sortSpend",
    defaultDir: "desc",
    getValue: (c) => c.spend,
  },
  clicks: {
    labelKey: "dashboard.roasWorld.creatives.clicks",
    defaultDir: "desc",
    getValue: (c) => c.clicks,
  },
  ctr: {
    labelKey: "dashboard.roasWorld.creatives.ctr",
    defaultDir: "desc",
    getValue: (c) => c.ctr,
  },
  cpc: {
    labelKey: "dashboard.roasWorld.creatives.sortCpc",
    defaultDir: "asc",
    getValue: (c) => c.cpc,
  },
  impressions: {
    labelKey: "dashboard.roasWorld.creatives.sortImpressions",
    defaultDir: "desc",
    getValue: (c) => c.impressions,
  },
  purchases: {
    labelKey: "dashboard.roasWorld.creatives.purchases",
    defaultDir: "desc",
    getValue: (c) => c.purchases,
  },
  costPerPurchase: {
    labelKey: "dashboard.roasWorld.creatives.costPerPurchase",
    defaultDir: "asc",
    getValue: (c) => c.costPerPurchase,
  },
  roas: {
    labelKey: "dashboard.roasWorld.creatives.roas",
    defaultDir: "desc",
    getValue: (c) => c.roas,
  },
  purchaseValue: {
    labelKey: "dashboard.roasWorld.creatives.revenue",
    defaultDir: "desc",
    getValue: (c) => c.purchaseValue,
  },
};

const SORT_KEYS: SortKey[] = [
  "rank",
  "leads",
  "costPerLead",
  "spend",
  "clicks",
  "ctr",
  "cpc",
  "impressions",
  "purchases",
  "costPerPurchase",
  "roas",
  "purchaseValue",
];

const HIDDEN_GEMS_MAX_SPEND_RATIO = 0.3; // Max 30% of median spend
const HIDDEN_GEMS_MIN_IMPRESSIONS = 100;

// ── Formatters ─────────────────────────────────────────

const fmtCurrency = (v: number): string =>
  v === 0
    ? "—"
    : v.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " zł";

const fmtNumber = (v: number): string => (v === 0 ? "—" : v.toLocaleString("pl-PL"));

const fmtPercent = (v: number): string => (v === 0 ? "—" : v.toFixed(2) + "%");

const fmtRoas = (v: number): string => (v === 0 ? "—" : v.toFixed(2) + "x");

// ── Hidden Gems Filter ────────────────────────────────

function findHiddenGems(creatives: RankedCreative[]): RankedCreative[] {
  if (creatives.length === 0) return [];

  const spends = creatives.map((c) => c.spend).sort((a, b) => a - b);
  const medianSpend = spends[Math.floor(spends.length / 2)];
  const maxSpend = medianSpend * HIDDEN_GEMS_MAX_SPEND_RATIO;

  return creatives
    .filter(
      (c) => c.spend > 0 && c.spend <= maxSpend && c.impressions >= HIDDEN_GEMS_MIN_IMPRESSIONS
    )
    .filter((c) => c.purchases > 0 || c.leads > 0)
    .sort((a, b) => {
      // Priority: purchases → leads → CTR
      if (a.purchases > 0 && b.purchases > 0) return a.costPerPurchase - b.costPerPurchase;
      if (a.purchases > 0) return -1;
      if (b.purchases > 0) return 1;
      if (a.leads > 0 && b.leads > 0) return a.costPerLead - b.costPerLead;
      if (a.leads > 0) return -1;
      if (b.leads > 0) return 1;
      return b.ctr - a.ctr;
    });
}

// ── Key Metric selector ────────────────────────────────

function getKeyMetric(creative: RankedCreative): {
  labelKey: string;
  value: string;
  highlight: boolean;
} {
  if (creative.purchases >= 2) {
    return {
      labelKey: "dashboard.roasWorld.creatives.costPerPurchase",
      value: fmtCurrency(creative.costPerPurchase),
      highlight: true,
    };
  }
  if (creative.leads >= 3) {
    return {
      labelKey: "dashboard.roasWorld.creatives.sortCostPerLead",
      value: fmtCurrency(creative.costPerLead),
      highlight: true,
    };
  }
  return {
    labelKey: "dashboard.roasWorld.creatives.ctr",
    value: fmtPercent(creative.ctr),
    highlight: false,
  };
}

// ── Creative Card ──────────────────────────────────────

function CreativeCard({
  creative,
  onGenerateVariation,
}: {
  creative: RankedCreative;
  onGenerateVariation: (creative: RankedCreative) => void;
}) {
  const { t } = useTranslation();
  const [previewError, setPreviewError] = useState(false);
  const keyMetric = getKeyMetric(creative);

  const previewUrl = creative.mediaType === "image" ? creative.imageUrl : creative.thumbnailUrl;

  const mediaLabel =
    creative.mediaType === "image"
      ? t("dashboard.roasWorld.creatives.labelGraphic")
      : t("dashboard.roasWorld.creatives.labelVideo");
  const MediaIcon = creative.mediaType === "image" ? Image : Video;

  return (
    <Card className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden hover:border-white/[0.12] transition-colors">
      {/* Preview */}
      <div className="relative aspect-video bg-black/30 flex items-center justify-center overflow-hidden">
        {previewUrl && !previewError ? (
          <img
            src={previewUrl}
            alt={creative.adName}
            className="w-full h-full object-cover"
            onError={() => setPreviewError(true)}
            loading="lazy"
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-white/20">
            <MediaIcon className="w-10 h-10" />
            <span className="text-xs">{t("dashboard.roasWorld.creatives.noPreview")}</span>
          </div>
        )}

        {/* Media type badge */}
        <div
          className={`absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] font-medium flex items-center gap-1 ${
            creative.mediaType === "video"
              ? "bg-purple-500/80 text-white"
              : "bg-blue-500/80 text-white"
          }`}
        >
          <MediaIcon className="w-3 h-3" />
          {mediaLabel}
        </div>

        {/* Key metric badge */}
        <div
          className={`absolute top-2 right-2 px-2 py-0.5 rounded text-[10px] font-bold ${
            keyMetric.highlight ? "bg-emerald-500/80 text-white" : "bg-white/20 text-white/80"
          }`}
        >
          {t(keyMetric.labelKey)}: {keyMetric.value}
        </div>
      </div>

      <CardContent className="p-3 space-y-2">
        {/* Ad name */}
        <div className="min-h-[32px]">
          <p className="text-xs font-medium text-white/90 line-clamp-2" title={creative.adName}>
            {creative.adName}
          </p>
          <p className="text-[10px] text-white/40 truncate mt-0.5" title={creative.campaignName}>
            {creative.campaignName}
          </p>
        </div>

        {/* Metrics grid */}
        <div className="grid grid-cols-3 gap-1.5">
          <MetricPill
            icon={DollarSign}
            label={t("dashboard.roasWorld.creatives.spent")}
            value={fmtCurrency(creative.spend)}
          />
          <MetricPill
            icon={Eye}
            label={t("dashboard.roasWorld.creatives.impressions")}
            value={fmtNumber(creative.impressions)}
          />
          <MetricPill
            icon={MousePointer}
            label={t("dashboard.roasWorld.creatives.clicks")}
            value={fmtNumber(creative.clicks)}
          />
          <MetricPill
            icon={TrendingUp}
            label={t("dashboard.roasWorld.creatives.ctr")}
            value={fmtPercent(creative.ctr)}
          />
          {creative.leads > 0 && (
            <MetricPill
              icon={Target}
              label={t("dashboard.roasWorld.creatives.sortLeads")}
              value={fmtNumber(creative.leads)}
            />
          )}
          {creative.purchases > 0 && (
            <MetricPill
              icon={ShoppingCart}
              label={t("dashboard.roasWorld.creatives.purchases")}
              value={fmtNumber(creative.purchases)}
            />
          )}
          {creative.roas > 0 && (
            <MetricPill
              icon={TrendingUp}
              label={t("dashboard.roasWorld.creatives.roas")}
              value={fmtRoas(creative.roas)}
              highlight
            />
          )}
          {creative.purchaseValue > 0 && (
            <MetricPill
              icon={DollarSign}
              label={t("dashboard.roasWorld.creatives.revenue")}
              value={fmtCurrency(creative.purchaseValue)}
              highlight
            />
          )}
        </div>

        {/* Primary text preview */}
        {creative.primaryText && (
          <p className="text-[10px] text-white/30 line-clamp-2 italic">
            "{creative.primaryText.slice(0, 120)}
            {creative.primaryText.length > 120 ? "…" : ""}"
          </p>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1.5 pt-1">
          <button
            onClick={() => onGenerateVariation(creative)}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-medium rounded-md bg-[#ef3e29]/20 text-[#ef3e29] hover:bg-[#ef3e29]/30 transition-colors"
          >
            <Wand2 className="w-3 h-3" />
            {t("dashboard.roasWorld.creatives.generateVariations")}
          </button>
          <a
            href={`https://www.facebook.com/ads/manager/account/ads/?act=${creative.adId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center p-1.5 rounded-md text-white/30 hover:text-white/60 hover:bg-white/5 transition-colors"
            title={t("dashboard.roasWorld.creatives.openInAdsManager")}
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Small Metric Pill ──────────────────────────────────

function MetricPill({
  icon: Icon,
  label,
  value,
  highlight = false,
}: {
  icon: typeof DollarSign;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`flex flex-col items-center gap-0.5 rounded-md px-1 py-1 ${
        highlight ? "bg-emerald-500/10" : "bg-white/[0.03]"
      }`}
    >
      <Icon className={`w-3 h-3 ${highlight ? "text-emerald-400" : "text-white/30"}`} />
      <span
        className={`text-[10px] font-medium ${highlight ? "text-emerald-400" : "text-white/70"}`}
      >
        {value}
      </span>
      <span className="text-[8px] text-white/30">{label}</span>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────

export default function BestCreatives({
  adAccountId,
  datePreset,
  customRange,
  forClientUserId,
  dashboardMode,
}: BestCreativesProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<CreativeFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("rank");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const handleSortKeyChange = (key: SortKey) => {
    setSortKey(key);
    setSortDir(SORT_CONFIG[key].defaultDir);
  };

  const toggleSortDir = () => {
    setSortDir((d) => (d === "asc" ? "desc" : "asc"));
  };

  // Always fetch max timeframe — best creatives are not tied to dashboard date range
  const requestBody = useMemo(() => {
    const body: Record<string, unknown> = { datePreset: "last_90d" };
    if (adAccountId) body.adAccountId = adAccountId;
    if (forClientUserId) body.forClientUserId = forClientUserId;
    return body;
  }, [adAccountId, forClientUserId]);

  const { data, isLoading } = useQuery<{ creatives: RankedCreative[] }>({
    queryKey: ["/api/dashboard/meta-ads/ranked-creatives", requestBody],
    queryFn: () => apiRequest("POST", "/api/dashboard/meta-ads/ranked-creatives", requestBody),
    staleTime: 1000 * 60 * 5,
  });

  const allCreatives = data?.creatives || [];

  // Apply filter
  const filteredCreatives = useMemo(() => {
    switch (filter) {
      case "image":
        return allCreatives.filter((c) => c.mediaType === "image");
      case "video":
        return allCreatives.filter((c) => c.mediaType === "video");
      case "hidden_gems":
        return findHiddenGems(allCreatives);
      default:
        return allCreatives;
    }
  }, [allCreatives, filter]);

  // Apply sorting
  const sortedCreatives = useMemo(() => {
    if (sortKey === "rank" && sortDir === "desc") return filteredCreatives;
    const getValue = SORT_CONFIG[sortKey].getValue;
    const sign = sortDir === "asc" ? 1 : -1;
    return [...filteredCreatives].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      // Push zero values to the bottom for ascending cost-style metrics
      // (e.g. cost/lead = 0 means "no leads", not "best")
      if (sortDir === "asc") {
        if (av === 0 && bv === 0) return 0;
        if (av === 0) return 1;
        if (bv === 0) return -1;
      }
      return sign * (av - bv);
    });
  }, [filteredCreatives, sortKey, sortDir]);

  // Stats
  const imageCount = allCreatives.filter((c) => c.mediaType === "image").length;
  const videoCount = allCreatives.filter((c) => c.mediaType === "video").length;
  const gemsCount = findHiddenGems(allCreatives).length;

  // Navigate to ad creator with creative context
  const handleGenerateVariation = (creative: RankedCreative) => {
    if (creative.mediaType === "image") {
      // Navigate to graphic ad creator with inspiration context
      const params = new URLSearchParams({
        inspiration: "true",
        adName: creative.adName,
        primaryText: creative.primaryText.slice(0, 500),
        headline: creative.headline,
        imageUrl: creative.imageUrl || "",
      });
      window.location.href = `/chat/ad-creator?mode=graphic&${params.toString()}`;
    } else {
      // For video — send transcription to text ad creator for inspiration
      const params = new URLSearchParams({
        inspiration: "true",
        adName: creative.adName,
        primaryText: creative.primaryText.slice(0, 500),
        headline: creative.headline,
        videoId: creative.videoId || "",
        source: "best-creatives",
      });
      window.location.href = `/chat/ad-creator?mode=text&${params.toString()}`;
    }
  };

  return (
    <div className="space-y-4">
      {/* Filter tabs */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1 p-1 bg-[#1a1614] border border-[#2e2624] rounded-lg">
          {(
            Object.entries(FILTER_CONFIG) as [
              CreativeFilter,
              (typeof FILTER_CONFIG)[CreativeFilter],
            ][]
          ).map(([key, config]) => {
            const Icon = config.icon;
            const count =
              key === "all"
                ? allCreatives.length
                : key === "image"
                  ? imageCount
                  : key === "video"
                    ? videoCount
                    : gemsCount;

            return (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                  filter === key
                    ? key === "hidden_gems"
                      ? "bg-amber-500/80 text-white"
                      : "bg-[#ef3e29] text-white"
                    : "text-white/50 hover:text-white hover:bg-[#2e2824]"
                }`}
              >
                <Icon className="w-3 h-3" />
                {t(config.labelKey)}
                {!isLoading && <span className="text-[10px] opacity-60">({count})</span>}
              </button>
            );
          })}
        </div>

        {/* Sort controls */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-white/40 uppercase tracking-wider">
            {t("dashboard.roasWorld.creatives.sortBy")}
          </span>
          <Select value={sortKey} onValueChange={(v) => handleSortKeyChange(v as SortKey)}>
            <SelectTrigger className="h-8 w-[180px] text-xs bg-[#1a1614] border-[#2e2624] text-white/80">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1614] border-[#2e2624]">
              {SORT_KEYS.map((key) => (
                <SelectItem
                  key={key}
                  value={key}
                  className="text-xs text-white/80 focus:bg-[#2e2824] focus:text-white"
                >
                  {t(SORT_CONFIG[key].labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            type="button"
            onClick={toggleSortDir}
            disabled={sortKey === "rank"}
            title={
              sortDir === "asc"
                ? t("dashboard.roasWorld.creatives.asc")
                : t("dashboard.roasWorld.creatives.desc")
            }
            className="h-8 w-8 flex items-center justify-center rounded-md bg-[#1a1614] border border-[#2e2624] text-white/70 hover:text-white hover:bg-[#2e2824] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {sortDir === "asc" ? (
              <ArrowUp className="w-3.5 h-3.5" />
            ) : (
              <ArrowDown className="w-3.5 h-3.5" />
            )}
          </button>
        </div>

        {/* Info label */}
        {filter === "hidden_gems" && (
          <p className="text-[10px] text-amber-400/60 max-w-xs">
            {t("dashboard.roasWorld.creatives.hiddenGemsHint")}
          </p>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-white/30" />
          <span className="ml-2 text-sm text-white/40">
            {t("dashboard.roasWorld.creatives.loading")}
          </span>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && sortedCreatives.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            {filter === "hidden_gems" ? (
              <>
                <Gem className="w-10 h-10 text-amber-400/20 mx-auto mb-3" />
                <p className="text-sm text-white/50">{t("dashboard.roasWorld.creatives.noGems")}</p>
                <p className="text-[10px] text-white/30 mt-1">
                  {t("dashboard.roasWorld.creatives.noGemsHint")}
                </p>
              </>
            ) : (
              <>
                <Image className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-sm text-white/50">
                  {t("dashboard.roasWorld.creatives.noCreatives")}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Creatives grid */}
      {!isLoading && sortedCreatives.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {sortedCreatives.map((creative) => (
            <CreativeCard
              key={creative.adId}
              creative={creative}
              onGenerateVariation={handleGenerateVariation}
            />
          ))}
        </div>
      )}

      {/* Summary stats */}
      {!isLoading && allCreatives.length > 0 && (
        <div className="flex items-center gap-4 text-[10px] text-white/30 pt-2 border-t border-white/[0.04]">
          <span className="flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            {sortKey === "rank"
              ? t("dashboard.roasWorld.creatives.sortDefaultHint")
              : t("dashboard.roasWorld.creatives.sortStatus", {
                  name: t(SORT_CONFIG[sortKey].labelKey),
                  direction:
                    sortDir === "asc"
                      ? t("dashboard.roasWorld.creatives.ascLower")
                      : t("dashboard.roasWorld.creatives.descLower"),
                })}
          </span>
          <span>
            {t("dashboard.roasWorld.creatives.summary", {
              total: allCreatives.length,
              images: imageCount,
              videos: videoCount,
            })}
          </span>
        </div>
      )}
    </div>
  );
}
